import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Video, Camera, Square, Copy, Link, Trash2, Play } from 'lucide-react'
import { useReactMediaRecorder } from 'react-media-recorder'
import { toast } from 'react-hot-toast'
import { authHeaders } from '../services/api';

const VideoRecorder = ({ quoteId, onVideoUploaded }) => {
  const [cameraStream, setCameraStream] = useState(null)
  const [cameraPosition, setCameraPosition] = useState('bottom-right')
  const [showCamera, setShowCamera] = useState(true)
  const [isRecording, setIsRecording] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const cameraRef = useRef(null)

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl
  } = useReactMediaRecorder({
    screen: true,
    audio: true,
    onStart: () => {
      setIsRecording(true)
      if (showCamera) {
        initializeCameraOverlay()
      }
    },
    onStop: (blobUrl, blob) => {
      setIsRecording(false)
      handleVideoComplete(blob)
    },
    mediaRecorderOptions: {
      mimeType: 'video/webm;codecs=vp9,opus',
      audioBitsPerSecond: 128000,
      videoBitsPerSecond: 2500000
    }
  })

  const initializeCameraOverlay = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 320 },
          height: { ideal: 240 },
          facingMode: 'user'
        },
        audio: false
      })
      
      setCameraStream(stream)
      if (cameraRef.current) {
        cameraRef.current.srcObject = stream
      }
    } catch (error) {
      console.error('Error accessing camera:', error)
      toast.error('Kamera konnte nicht aktiviert werden')
    }
  }, [])

  const handleVideoComplete = async (blob) => {
    // Stop camera stream
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop())
      setCameraStream(null)
    }

    if (!blob) {
      toast.error('Video-Aufnahme fehlgeschlagen')
      return
    }

    // Upload video to backend
    const formData = new FormData()
    formData.append('video', blob, `angebot-video-${quoteId}-${Date.now()}.webm`)
    formData.append('quote_id', quoteId)
    formData.append('title', `Angebots-Erklärung für Quote ${quoteId}`)

    try {
      setUploadProgress(10)
      
      const response = await fetch('/api/videos/upload', {
        method: 'POST',
        headers: authHeaders(),  // kein Content-Type: FormData setzt die Boundary selbst
        body: formData
      })

      setUploadProgress(90)

      const result = await response.json()
      
      if (response.ok && result.video_id) {
        setUploadProgress(100)
        toast.success('Video erfolgreich hochgeladen!')
        
        // Clear the blob URL to free memory
        clearBlobUrl()
        
        // Notify parent component
        if (onVideoUploaded) {
          onVideoUploaded({
            id: result.video_id,
            url: result.url,
            shareLink: result.share_link,
            title: result.title || 'Angebots-Video',
            createdAt: new Date().toISOString()
          })
        }
      } else {
        throw new Error(result.detail || 'Upload failed')
      }
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Video-Upload fehlgeschlagen: ' + error.message)
    } finally {
      setUploadProgress(0)
    }
  }

  const handleStartRecording = async () => {
    try {
      // Check browser support
      if (!navigator.mediaDevices?.getDisplayMedia) {
        toast.error('Bildschirmaufnahme wird in diesem Browser nicht unterstützt')
        return
      }

      await startRecording()
    } catch (error) {
      console.error('Recording start failed:', error)
      toast.error('Aufnahme konnte nicht gestartet werden')
    }
  }

  const getCameraOverlayStyle = () => {
    const baseStyle = {
      position: 'absolute',
      width: '200px',
      height: '150px',
      borderRadius: '50%',
      border: '3px solid white',
      zIndex: 1000,
      objectFit: 'cover'
    }

    switch (cameraPosition) {
      case 'bottom-left':
        return { ...baseStyle, bottom: '20px', left: '20px' }
      case 'bottom-right':
        return { ...baseStyle, bottom: '20px', right: '20px' }
      case 'top-left':
        return { ...baseStyle, top: '20px', left: '20px' }
      case 'top-right':
        return { ...baseStyle, top: '20px', right: '20px' }
      default:
        return { ...baseStyle, bottom: '20px', right: '20px' }
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop())
      }
    }
  }, [cameraStream])

  return (
    <div className="relative">
      {/* Recording Controls */}
      <div className="flex items-center space-x-3 mb-4">
        {status === 'idle' && (
          <Button
            onClick={handleStartRecording}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Video className="h-4 w-4 mr-2" />
            Video-Erklärung aufnehmen
          </Button>
        )}

        {status === 'recording' && (
          <div className="flex items-center space-x-3">
            <Button
              onClick={stopRecording}
              className="bg-red-600 hover:bg-red-700 text-white animate-pulse"
            >
              <Square className="h-4 w-4 mr-2" />
              Aufnahme beenden
            </Button>
            <Badge className="bg-red-100 text-red-800">
              🔴 Aufnahme läuft...
            </Badge>
          </div>
        )}

        {status === 'stopped' && mediaBlobUrl && (
          <div className="flex items-center space-x-3">
            <Badge className="bg-green-100 text-green-800">
              ✅ Aufnahme abgeschlossen
            </Badge>
            {uploadProgress > 0 && (
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Camera Controls - Only show during recording setup */}
      {(status === 'idle' || status === 'recording') && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-sm flex items-center">
              <Camera className="h-4 w-4 mr-2" />
              Kamera-Einstellungen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showCamera}
                onChange={(e) => setShowCamera(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Kamera-Overlay anzeigen</span>
            </div>
            
            {showCamera && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Kamera-Position:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'top-left', label: 'Oben Links' },
                    { value: 'top-right', label: 'Oben Rechts' },
                    { value: 'bottom-left', label: 'Unten Links' },
                    { value: 'bottom-right', label: 'Unten Rechts' }
                  ].map(pos => (
                    <Button
                      key={pos.value}
                      size="sm"
                      variant={cameraPosition === pos.value ? "default" : "outline"}
                      onClick={() => setCameraPosition(pos.value)}
                      className="text-xs"
                    >
                      {pos.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Camera Overlay during recording */}
      {isRecording && showCamera && cameraStream && (
        <video
          ref={cameraRef}
          style={getCameraOverlayStyle()}
          autoPlay
          muted
          playsInline
        />
      )}

      {/* Video Preview after recording */}
      {status === 'stopped' && mediaBlobUrl && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-sm">Video-Vorschau</CardTitle>
          </CardHeader>
          <CardContent>
            <video 
              src={mediaBlobUrl} 
              controls 
              className="w-full max-w-md rounded-lg"
            />
            <div className="mt-3 flex space-x-2">
              <Button
                size="sm"
                onClick={() => {
                  clearBlobUrl()
                  setUploadProgress(0)
                }}
                variant="outline"
              >
                Neue Aufnahme
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default VideoRecorder